import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ArrowUpRight } from 'lucide-react';
import { useTranslation } from '../../../../lib/i18n';
import { useProductName } from '../../../../lib/use-brand';
import { useGenericBi } from '../../../../lib/use-generic-bi';
import { useItemSeenBi } from '../../../../lib/use-item-seen-bi';
import { queryKeys } from '../../../../lib/query-client';
import { connectorDescriptionKey, fetchConnectors, fetchShopifyConnectStatus } from '../../../../lib/connectors';
import { connectorsPath } from '../../../../lib/connectors-route';
import { APPS_SCRIPT_SETUP_PATH } from '../../../../lib/apps-script-setup';
import {
  CONNECT_POLL_INTERVAL_MS,
  connectDismissedStorageKey,
  connectTapStorageKey,
  deriveConnectorCardState,
  isToolkitConnected,
  msUntilConnectorCardExpiry,
  readShopifyConnectStatus,
  shouldPollWhileWaiting,
  shopifyCardAttemptId,
  type ConnectorCardData,
} from '../../../../lib/connector-card';
import { shouldArmConnectHandoff } from '../../../../lib/connect-handoff';
import { ConnectorLogo } from '../../connectors/ConnectorLogo';
import { Spinner } from '../../../Spinner';
import { Button, Card, TextButton, Typography } from '../../ui';
import { CHAT_HOME_BI } from '../message-utils';
import styles from './ConnectorConnectCard.module.css';

// The in-chat connector connect card — rendered under an assistant reply carrying
// `metadata.connectorCard` (a turn whose ConnectApp tool minted a Composio OAuth link).
// The reply text deliberately carries no URL, so this card is the only way to connect.
// It is LIVE: state derives from the shared connectors status data + the local tap
// marker + the link's age (see lib/connector-card.ts — connected > expired > waiting >
// offered). All pure logic lives in that dependency-free module.

// Lucide's `check`, scaled into the 24px disc — same resting badge as MilestoneCard.
const CHECK_PATH = 'M6.67 12 L10 15.33 L17.33 8';

// A store name or domain sits inside a localized, possibly RTL sentence. It is isolated at its own
// placeholder: searching the finished sentence for it would also wrap look-alike letters in the static words.
const VALUE_MARKER = '\uE000';

function IsolatedInSentence({ line, value }: { line: (value: string) => string; value: string }) {
  const parts = line(VALUE_MARKER).split(VALUE_MARKER);
  if (parts.length !== 2) return <>{line(value)}</>;
  return <>{parts[0]}<bdi>{value}</bdi>{parts[1]}</>;
}

function readTapMarker(messageId: string | undefined): number | null {
  if (!messageId || typeof window === 'undefined') return null;
  try {
    const value = window.localStorage.getItem(connectTapStorageKey(messageId));
    if (!value) return null;
    const at = Date.parse(value);
    // A marker we can't parse still means "this browser tapped Connect" — it just can't
    // scope the poll window, so it doesn't poll (focus refetch still covers it).
    return Number.isFinite(at) ? at : 0;
  } catch {
    return null;
  }
}

function readDismissed(messageId: string | undefined): boolean {
  if (!messageId || typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(connectDismissedStorageKey(messageId)) === 'true';
  } catch {
    return false;
  }
}

export function ConnectorConnectCard({
  card,
  messageId,
  onRequestNewLink,
  onConnected,
  canSend = true,
}: {
  card: ConnectorCardData;
  /** The row's persisted id (finalId when streamed) — scopes the tap marker to this message. */
  messageId?: string;
  /** Expired state's "Get a new link": send the localized ask as a normal user turn. Absent (read-only surface) => line only. */
  onRequestNewLink?: (text: string) => void;
  /** Fired the moment this card observes the connect, with the connector's display name — the transcript shows a "Inspecting your {name}." thinking line while the genome wakes the loop. */
  onConnected?: (name: string) => void;
  canSend?: boolean;
}) {
  const { t } = useTranslation();
  const productName = useProductName();
  const { logPageClickBi } = useGenericBi();
  const [tappedAt, setTappedAt] = useState<number | null>(() => readTapMarker(messageId));
  const tapped = tappedAt !== null;
  const shopifyAttemptId = shopifyCardAttemptId(card);
  const [dismissed, setDismissed] = useState(() => readDismissed(messageId));
  const [expanded, setExpanded] = useState(false);
  useEffect(() => {
    setDismissed(readDismissed(messageId));
    setExpanded(false);
  }, [messageId]);
  const isAppsScript = card.toolkit.toLowerCase() === 'google-apps-script';
  const [graceElapsed, setGraceElapsed] = useState(false);
  // Match the native card: returning from authorization restores the action after 10 seconds,
  // without clearing the tap marker or manufacturing connection completion.
  useEffect(() => {
    setGraceElapsed(false);
    if (tappedAt === null) return undefined;
    let timer: number | undefined;
    const arm = () => {
      if (document.visibilityState === 'hidden') return;
      if (timer !== undefined) window.clearTimeout(timer);
      timer = window.setTimeout(() => setGraceElapsed(true), shopifyAttemptId ? 15_000 : 10_000);
    };
    arm();
    window.addEventListener('focus', arm);
    document.addEventListener('visibilitychange', arm);
    return () => {
      if (timer !== undefined) window.clearTimeout(timer);
      window.removeEventListener('focus', arm);
      document.removeEventListener('visibilitychange', arm);
    };
  }, [tappedAt, shopifyAttemptId]);
  // Bumped when the link's deadline passes. The state below is derived from `Date.now()` during render, so
  // the bump is what re-renders it onto "Get a new link"; nothing else would (see msUntilConnectorCardExpiry).
  const [expiryTick, setExpiryTick] = useState(0);

  // Whether this card is still trying to observe a connect (drives the focus refetch and
  // the 5s poll). Derived WITHOUT the status data — the data-aware check runs inside
  // `refetchInterval`, which sees the freshest read and stops the moment it lands.
  const awaitingConnect =
    deriveConnectorCardState({ connected: false, tapped, mintedAt: card.mintedAt, expiresAt: card.expiresAt, now: Date.now() }) === 'waiting';

  const { data } = useQuery({
    queryKey: queryKeys.connectors,
    queryFn: fetchConnectors,
    enabled: !shopifyAttemptId && !isAppsScript,
    refetchOnWindowFocus: awaitingConnect,
    refetchInterval: (latest) => {
      if (!awaitingConnect || tappedAt === null) return false;
      if (latest && isToolkitConnected(latest.connectors, card.toolkit, card.reconnect)) return false;
      return shouldPollWhileWaiting(tappedAt, Date.now()) ? CONNECT_POLL_INTERVAL_MS : false;
    },
  });

  const { data: shopifyStatus } = useQuery({
    queryKey: queryKeys.shopifyConnectStatus(shopifyAttemptId),
    queryFn: () => {
      if (!shopifyAttemptId) throw new Error('missing_shopify_attempt');
      return fetchShopifyConnectStatus(shopifyAttemptId);
    },
    enabled: Boolean(shopifyAttemptId),
    // A replacement overwrites the server's current intent, not this completed attempt's receipt.
    structuralSharing: (previous, next) => {
      const receipt = readShopifyConnectStatus(previous);
      return previous !== undefined && receipt?.status === 'connected' ? previous : next;
    },
    refetchOnWindowFocus: true,
    refetchInterval: (latest) => shopifyAttemptId && tappedAt !== null && latest?.status !== 'connected' &&
      shouldPollWhileWaiting(tappedAt, Date.now()) ? CONNECT_POLL_INTERVAL_MS : false,
  });

  const connected = !isAppsScript && (shopifyAttemptId ? shopifyStatus?.status === 'connected' : isToolkitConnected(data?.connectors, card.toolkit, card.reconnect));
  const state = isAppsScript ? 'offered' : shopifyStatus?.status === 'expired' ? 'expired' :
    deriveConnectorCardState({ connected, tapped, graceElapsed: graceElapsed || shopifyStatus?.status === 'failed',
      mintedAt: card.mintedAt, expiresAt: card.expiresAt, now: Date.now() });

  // Which connector, which flow put it here (`shopify-landing` for the landing-page offer), and the state it
  // was seen in, so "was it shown" and "did they tap Connect" join on the same card. Observed on ONE wrapper
  // that survives the state changes, so a tap moving the card to waiting or connected is not a second sighting.
  const biFields = { toolkit: card.toolkit, source: card.source, messageId };
  const seenRef = useItemSeenBi('connector-connect-card', { additionalFields: { ...biFields, state } });

  // Both a live transition and a FIRST read that is already connected arm the handoff, but only on a recent
  // tap marker of this browser's own — the reload-after-authorizing case, where the ref has been reset and
  // there is no transition left to see. The marker is one-shot-cleared from localStorage the moment the
  // handoff arms, so RELOADING into this (by then settled) chat within the 10-min window does not re-arm and
  // re-show the placeholder over a turn that already ran, and a thread reopened later still spins nothing.
  const wasConnectedRef = useRef<boolean | undefined>(undefined);
  useEffect(() => {
    if (shouldArmConnectHandoff(wasConnectedRef.current, connected, tappedAt)) {
      onConnected?.(card.name);
      // One-shot: retire the tap marker the moment the handoff arms — from localStorage AND from the
      // in-memory state. Clearing only storage leaves `tappedAt` set for this mount, so a later
      // connected:false→true flip (a refetch blip) would re-arm and re-show the placeholder over a turn
      // that already answered. Nulling the state mirrors native, which re-reads the (now-cleared) marker
      // fresh at each arm. The card is already `connected`, so dropping the tap does not disturb its state.
      if (messageId) {
        try {
          window.localStorage.removeItem(connectTapStorageKey(messageId));
        } catch {
          // storage unavailable — the in-memory clear below still settles this session's card
        }
      }
      setTappedAt(null);
    }
    wasConnectedRef.current = connected;
  }, [connected, onConnected, tappedAt, card.name, messageId]);

  // Wake once at the link's deadline so a card left on screen flips to "Get a new link" by itself.
  useEffect(() => {
    if (connected) return undefined;
    const wait = msUntilConnectorCardExpiry({ mintedAt: card.mintedAt, expiresAt: card.expiresAt }, Date.now());
    if (wait === null) return undefined;
    const timer = window.setTimeout(() => setExpiryTick((tick) => tick + 1), wait);
    return () => window.clearTimeout(timer);
  }, [connected, card.mintedAt, card.expiresAt, expiryTick]);

  const onConnect = (sameTab = false) => {
    // A link that died since the last render (a throttled background-tab timer) must not open: re-render
    // onto "Get a new link" instead.
    if (deriveConnectorCardState({ connected: false, tapped, mintedAt: card.mintedAt, expiresAt: card.expiresAt, now: Date.now() }) === 'expired') {
      setExpiryTick((tick) => tick + 1);
      return;
    }
    logPageClickBi({ ...CHAT_HOME_BI, button_name: 'connector-card-connect' }, { additionalFields: biFields });
    // Not `noopener`: the "Connected!" page closes its own tab, and a browser lets only a tab that script
    // opened do that. The tab opens blank and loses its `opener` before it navigates, so the vendor's pages
    // never get a handle on this one.
    const tab = sameTab ? null : window.open('', '_blank');
    if (tab) {
      tab.opener = null;
      tab.location.href = card.connectUrl;
    }
    const now = Date.now();
    if (messageId) {
      try {
        window.localStorage.setItem(connectTapStorageKey(messageId), new Date(now).toISOString());
      } catch {
        // storage unavailable — the in-memory tap still enters waiting for this session
      }
    }
    setTappedAt(now);
    if (shopifyAttemptId && (sameTab || !tab)) window.location.assign(card.connectUrl);
  };

  const onGetNewLink = () => {
    logPageClickBi({ ...CHAT_HOME_BI, button_name: 'connector-card-new-link' }, { additionalFields: biFields });
    onRequestNewLink?.(t('chat.connectorCard.renewMessage', { name: card.name }));
  };

  const onBrowseAll = () => {
    logPageClickBi({ ...CHAT_HOME_BI, button_name: 'connector-card-browse-all' }, { additionalFields: biFields });
  };

  const onNotNow = () => {
    if (!canSend) return;
    logPageClickBi({ ...CHAT_HOME_BI, button_name: 'shopify-card-not-now' }, { additionalFields: biFields });
    setExpanded(false);
    if (dismissed) return;
    setDismissed(true);
    if (messageId) {
      try {
        window.localStorage.setItem(connectDismissedStorageKey(messageId), 'true');
      } catch {
        // The in-memory state still keeps this card collapsed when storage is unavailable.
      }
    }
    onRequestNewLink?.(t('chat.shopifyConnect.notNowMessage'));
  };

  if (state === 'connected') {
    return (
      <div ref={seenRef}>
        <Card className={`${styles.card} ${styles.connected}`}>
          <div className={styles.row}>
            <svg className={styles.badge} width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden focusable="false">
              <circle className={styles.disc} cx="12" cy="12" r="12" />
              <path className={styles.check} d={CHECK_PATH} />
            </svg>
            <div className={styles.text}>
              <Typography variant="h4" component="div" color="strong">
                {shopifyStatus?.shopName
                  ? <IsolatedInSentence line={(name) => t('chat.connectorCard.connectedTitle', { name })} value={shopifyStatus.shopName} />
                  : t('chat.connectorCard.connectedTitle', { name: card.name })}
              </Typography>
              <Typography variant="p3" color="strong">
                {shopifyStatus?.shop
                  ? <IsolatedInSentence line={(domain) => t('chat.shopifyConnect.connectedApproval', { domain })} value={shopifyStatus.shop} />
                  : t('chat.connectorCard.connectedBody')}
              </Typography>
              {shopifyAttemptId && onRequestNewLink && (
                <TextButton className={styles.switchStore} size="small" disabled={!canSend} onClick={() => {
                  if (!canSend) return;
                  logPageClickBi({ ...CHAT_HOME_BI, button_name: 'shopify-card-switch-store' }, { additionalFields: biFields });
                  onRequestNewLink(t('chat.shopifyConnect.switchMessage'));
                }}>{t('chat.shopifyConnect.switchStore')}</TextButton>
              )}
            </div>
          </div>
        </Card>
      </div>
    );
  }

  if (shopifyAttemptId && dismissed && !expanded && state !== 'waiting') {
    return (
      <div ref={seenRef}>
        <Card className={`${styles.card} ${styles.shopify}`}>
          <div className={styles.row}>
            <Typography variant="p3" color="secondary">{t('chat.shopifyConnect.postponed')}</Typography>
            <TextButton size="small" onClick={() => {
              logPageClickBi({ ...CHAT_HOME_BI, button_name: 'shopify-card-resume' }, { additionalFields: biFields });
              setExpanded(true);
            }}>{t('chat.shopifyConnect.continue')}</TextButton>
          </div>
        </Card>
      </div>
    );
  }

  const descriptionKey = connectorDescriptionKey({ toolkit: card.toolkit, connected: false });
  const localizedDescription = descriptionKey ? t(descriptionKey, { productName }) : null;
  const supportingLine =
    shopifyAttemptId && shopifyStatus?.status === 'failed'
      ? t('chat.shopifyConnect.unfinished')
      : shopifyAttemptId && tapped && graceElapsed
      ? t('chat.shopifyConnect.unfinished')
      : state === 'waiting'
      ? t('chat.connectorCard.waiting')
      : state === 'expired'
      ? t('chat.connectorCard.expired')
      : shopifyAttemptId
      ? t('chat.shopifyConnect.signIn')
      : localizedDescription && localizedDescription !== descriptionKey
      ? localizedDescription
      : t('chat.connectorCard.genericDescription');

  return (
    // Per Figma the "Browse all connectors" CTA sits OUTSIDE the card, ~12px beneath it — so the card
    // wraps only the connect row now and the text button is its sibling (see `.browse`). Mirrors iOS/Android.
    <div ref={seenRef}>
      <Card className={`${styles.card} ${shopifyAttemptId ? styles.shopify : ''}`}>
        <div className={styles.row}>
          {/* The DEFAULT `--connector-plate` tile — the same neutral 28/18 tile the Connectors screen
              rows and the connected-tools deck draw. It used to override to a fixed white
              (native's `connectorBadgePlate`), which on the card's own white surface read as no tile at
              all while the identical row on Connectors had one. Changed on all three surfaces together
              (iOS `logoTile`, Android `ConnectorCardLogo`). */}
          <ConnectorLogo
            connector={{ toolkit: card.toolkit, logo: card.logoUrl, connected: false }}
            name={card.name}
            size={28}
            glyphSize={18}
            radius={8}
          />
          <div className={styles.text}>
            <Typography variant="h4" component="div" color="strong">
              {card.name}
            </Typography>
            <Typography variant="p3" color="secondary">
              {supportingLine}
            </Typography>
          </div>
          {state === 'waiting' ? (
            <span className={styles.spinnerSlot} role="status" aria-label={t('common.connecting')}>
              <Spinner size={18} />
            </span>
          ) : state === 'expired' ? (
            onRequestNewLink && (
              <Button className={styles.cta} size="small" onClick={onGetNewLink}>
                {t('chat.connectorCard.getNewLink')}
              </Button>
            )
          ) : (
            isAppsScript ? <Button className={styles.cta} size="small" href={APPS_SCRIPT_SETUP_PATH}>
              {t('appsScript.setup.refresh')}
            </Button> : <Button className={styles.cta} size="small" onClick={() => onConnect()}>
              {shopifyAttemptId ? t('chat.shopifyConnect.continue') : t('common.connect')}
            </Button>
          )}
        </div>
        {shopifyAttemptId && tapped && graceElapsed && state !== 'expired' && (
          <TextButton size="small" onClick={() => onConnect(true)}>{t('chat.shopifyConnect.sameTab')}</TextButton>
        )}
        {shopifyAttemptId && state === 'offered' && onRequestNewLink && (
          <TextButton size="small" disabled={!canSend} onClick={onNotNow}>{t('chat.shopifyConnect.notNow')}</TextButton>
        )}
      </Card>
      <TextButton
        className={styles.browse}
        size="small"
        href={connectorsPath({ tab: 'tools' })}
        onClick={onBrowseAll}
        endIcon={<ArrowUpRight size={16} strokeWidth={1.5} absoluteStrokeWidth aria-hidden />}
      >
        {t('chat.connectorCard.browseAll')}
      </TextButton>
    </div>
  );
}
